import React from "react";
import {
  Card,
  FormGroup,
  HTMLSelect,
  InputGroup,
  Button,
  Tag,
  Spinner,
  NonIdealState,
  MenuItem,
} from "@blueprintjs/core";
import { Suggest } from "@blueprintjs/select";
import { inject, observer } from "mobx-react";
import { apiCallPost } from "../../micro/fns/util";
import cogoToast from "cogo-toast";
import _ from "lodash";
import SingleSelectWrapper from "../../micro/utils/SingleSelectWrapper";

class ItemSelectionDropdown extends React.Component {
  state = {
    // Item search and selection
    searchTerm: "",
    isSearching: false,
    searchResults: [],
    selectedItem: null,

    // Category filtering
    selectedCategory: "",
    availableCategories: [],
    isLoadingCategories: false,

    // Variant handling
    variantValues: {},
    isVariantSelectionComplete: false,
    showAddButton: false,

    // UI states
    showInactive: false,
  };

  componentDidMount() {
    this.loadAvailableCategories();

    // If an initial item is provided, set it
    if (this.props.initialItem) {
      this.setState({ selectedItem: this.props.initialItem });
    }
  }

  // Load available item categories
  loadAvailableCategories = async () => {
    try {
      this.setState({ isLoadingCategories: true });

      // First, get all items to extract unique categories
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/item/list`, {
        showInactive: false,
      });

      if (response && response.entities) {
        const categories = [...new Set(response.entities.map((item) => item.itemCategory))].filter(Boolean);
        this.setState({ availableCategories: categories });
      }
    } catch (err) {
      console.error("Failed to load categories:", err);
      cogoToast.error("Failed to load item categories");
    } finally {
      this.setState({ isLoadingCategories: false });
    }
  };

  // Search for items based on name and filters
  searchItems = async (searchTerm = this.state.searchTerm) => {
    if (!searchTerm.trim() && !this.state.selectedCategory) {
      this.setState({ searchResults: [] });
      return;
    }

    try {
      this.setState({ isSearching: true });

      const params = {
        showInactive: this.state.showInactive,
      };

      if (searchTerm.trim()) {
        params.itemName = searchTerm.trim();
      }

      if (this.state.selectedCategory) {
        params.itemCategory = this.state.selectedCategory;
      }

      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/item/list`, params);

      if (response && response.entities) {
        this.setState({ searchResults: response.entities });
      } else {
        this.setState({ searchResults: [] });
      }
    } catch (err) {
      console.error("Failed to search items:", err);
      cogoToast.error("Failed to search items");
      this.setState({ searchResults: [] });
    } finally {
      this.setState({ isSearching: false });
    }
  };

  // Handle item selection
  handleItemSelect = (item) => {
    // Check if there's already a selected item with incomplete variants
    if (this.state.selectedItem && this.state.selectedItem.hasVariants && !this.state.isVariantSelectionComplete) {
      cogoToast.warning("Please complete variant selection for the current item before selecting another");
      return;
    }

    console.log("Selected item:", item); // Debug log

    // Check if item has variants
    const hasVariants = item.hasVariants && item.numApplicableVariantProperties > 0;

    this.setState({
      selectedItem: item,
      variantValues: {}, // Reset variant values when new item is selected
      isVariantSelectionComplete: !hasVariants, // If no variants, it's complete
      showAddButton: !hasVariants, // Show add button if no variants needed
      searchTerm: "", // Clear search after selection
      searchResults: [], // Clear search results
    });

    // Notify parent component only if variants are complete or not needed
    if (this.props.onItemSelect && !hasVariants) {
      this.props.onItemSelect(item);
    }
  };

  // Check if all required variants are selected
  areAllVariantsSelected = () => {
    const item = this.state.selectedItem;
    if (!item || !item.variantProperties || item.variantProperties.length === 0) {
      return true; // No variants required
    }

    const requiredCount = this.getRequiredVariantCount();
    if (requiredCount === 0) return true;

    // Count only the variants for visible properties
    let selectedVisibleCount = 0;
    item.variantProperties.forEach((prop, index) => {
      if (this.shouldShowVariantProperty(prop, index)) {
        const isCyl = (prop?.name || "").toLowerCase().includes("cyl");
        const value = this.state.variantValues[`property${index + 1}`];
        if (value && !isCyl) selectedVisibleCount++;
      }
    });

    return selectedVisibleCount === requiredCount;
  };

  // Check variant completion for real API structure
  checkVariantCompletion = (variantValues) => {
    const item = this.state.selectedItem;
    if (!item || !item.hasVariants) return true;

    const numProperties = item.numApplicableVariantProperties || 0;
    let requiredCount = 0;
    let selectedCount = 0;

    // Count required and selected variants
    for (let i = 1; i <= numProperties; i++) {
      const name = item[`variantProperty${i}Name`];
      const values = item[`variantProperty${i}Values`];

      if (name && values && values.length > 0) {
        // Check if this property should be shown
        const prop = { name, values, index: i };
        if (this.shouldShowVariantProperty(prop, i - 1)) {
          const isCyl = (name || "").toLowerCase().includes("cyl");
          // CYL is optional: do not include it in required or selected counts
          if (!isCyl) {
            requiredCount++;
            const value = variantValues[`property${i}`];
            if (value && value.trim() !== "") {
              selectedCount++;
            }
          }
        }
      }
    }

    return selectedCount === requiredCount;
  };

  // Handle variant property change
  handleVariantChange = (propertyIndex, value) => {
    const newVariantValues = {
      ...this.state.variantValues,
      [`property${propertyIndex + 1}`]: value,
    };

    // Apply conditional logic based on selected values
    const processedVariants = this.applyVariantConditionalLogic(newVariantValues, propertyIndex, value);

    // Check if all variants are now complete
    const isComplete = this.checkVariantCompletion(processedVariants);

    this.setState({
      variantValues: processedVariants,
      isVariantSelectionComplete: isComplete,
      showAddButton: isComplete,
    });

    // Notify parent component about variant changes
    if (this.props.onVariantChange) {
      this.props.onVariantChange(this.state.selectedItem, processedVariants);
    }

    // If all variants are complete, show success message
    if (isComplete) {
      cogoToast.success("All variants selected! You can now add this item.");
    }
  };

  // Apply conditional logic for variant dependencies
  applyVariantConditionalLogic = (variantValues, changedIndex, changedValue) => {
    const item = this.state.selectedItem;
    if (!item || !item.variantProperties) return variantValues;

    const processedValues = { ...variantValues };
    const changedProperty = item.variantProperties[changedIndex];

    // Handle spherical vs cylindrical lens logic
    if (changedProperty && changedProperty.name.toLowerCase().includes("sph")) {
      // If spherical power is selected
      if (changedValue && changedValue !== "0.00D" && !changedValue.includes("0.00")) {
        // This is a spherical lens, might not need cylinder
        console.log("Spherical lens selected:", changedValue);
      }
    }

    if (changedProperty && changedProperty.name.toLowerCase().includes("cyl")) {
      // If cylindrical power is selected
      if (changedValue && changedValue !== "0.00D" && !changedValue.includes("0.00")) {
        // This is a cylindrical lens
        console.log("Cylindrical lens selected:", changedValue);
      } else if (changedValue === "0.00D" || changedValue.includes("0.00")) {
        // No cylinder needed, might clear axis
        const axisPropertyIndex = item.variantProperties.findIndex(
          (prop) => prop.name.toLowerCase().includes("axis") || prop.name.toLowerCase().includes("tst")
        );
        if (axisPropertyIndex !== -1) {
          // Clear axis when cylinder is 0
          delete processedValues[`property${axisPropertyIndex + 1}`];
        }
      }
    }

    return processedValues;
  };

  // ===== ALL POSSIBLE VARIANT PROPERTIES =====
  //
  // OPTICAL PROPERTIES:
  // - SPH/SPHERE: Spherical power (-20.00D to +20.00D)
  // - CYL/CYLINDER: Cylindrical power (0.00D to -6.00D)
  // - AXIS/TST: Axis angle (0° to 180°)
  // - ADD: Addition power for multifocal (+1.00D to +3.50D)
  // - PRISM: Prism correction (0 to 10 prism diopters)
  //
  // PHYSICAL PROPERTIES:
  // - DIAMETER/DIA: Lens diameter (13.0mm to 15.0mm for contacts)
  // - BASE_CURVE/BC: Base curve (8.0mm to 10.0mm for contacts)
  // - THICKNESS: Lens thickness (0.1mm to 2.0mm)
  // - SIZE: Small/Medium/Large/XL
  //
  // MATERIAL PROPERTIES:
  // - MATERIAL: Silicone Hydrogel, PMMA, Acrylic, etc.
  // - BRAND: Acuvue, Biofinity, AcrySof, etc.
  // - COLOR: Clear, Blue, Brown, Green, etc.
  // - COATING: Anti-reflective, UV, Blue light, etc.
  //
  // IOL SPECIFIC:
  // - IOL_POWER: IOL power (+5.0D to +35.0D)
  // - IOL_TYPE: Monofocal, Multifocal, Toric, etc.
  // - HAPTIC_DESIGN: C-loop, Plate, etc.
  // - A_CONSTANT: A-constant for IOL calculation
  //
  // CONTACT LENS SPECIFIC:
  // - REPLACEMENT: Daily, Weekly, Monthly, Yearly
  // - WATER_CONTENT: 38%, 55%, etc.
  // - DK_T: Oxygen transmissibility
  // - MODULUS: Lens flexibility
  //
  // SURGICAL SUPPLIES:
  // - GAUGE: Needle gauge (25G, 27G, etc.)
  // - LENGTH: Length in mm or inches
  // - STERILITY: Sterile, Non-sterile
  // - PACK_SIZE: 1, 5, 10, 100 pieces
  //
  // Check if a variant property should be displayed based on other selections
  shouldShowVariantProperty = (prop, index) => {
    const item = this.state.selectedItem;
    if (!item) return true;

    const propName = prop.name.toLowerCase();
    const currentValues = this.state.variantValues;

    // Handle axis/TST field - only show if cylinder is not 0
    if (propName.includes("axis") || propName.includes("tst")) {
      // For mock variants, CYL is at index 1 (property2)
      const cylValue = currentValues[`property2`];
      // Hide axis if cylinder is 0 or not selected
      if (!cylValue || cylValue === "0.00D" || cylValue.includes("0.00") || cylValue === "0") {
        return false;
      }
    }

    // Handle cylinder field based on lens type
    if (propName.includes("cyl")) {
      // Check if this is a spherical-only lens based on item name or other properties
      const itemName = item.itemName.toLowerCase();

      // Hide cylinder for purely spherical lenses
      if (itemName.includes("spherical") && !itemName.includes("toric")) {
        return false;
      }

      // Show cylinder for toric lenses
      if (itemName.includes("toric")) {
        return true;
      }
    }

    // Handle add power - only show for multifocal lenses
    if (propName.includes("add")) {
      const itemName = item.itemName.toLowerCase();

      // Only show ADD for multifocal/progressive lenses
      if (
        itemName.includes("multifocal") ||
        itemName.includes("progressive") ||
        itemName.includes("bifocal") ||
        itemName.includes("trifocal")
      ) {
        return true;
      }

      // Hide ADD for single vision lenses
      return false;
    }

    // Handle diameter - might be conditional based on lens type
    if (propName.includes("diameter") || propName.includes("dia")) {
      // Always show diameter for contact lenses
      const itemName = item.itemName.toLowerCase();
      if (itemName.includes("contact") || itemName.includes("lens")) {
        return true;
      }
    }

    // Handle base curve - mainly for contact lenses
    if (propName.includes("base") || propName.includes("bc")) {
      const itemName = item.itemName.toLowerCase();
      if (itemName.includes("contact") || itemName.includes("lens")) {
        return true;
      }
      // Hide for IOLs unless specifically needed
      return !itemName.includes("iol");
    }

    // Handle prism - only for special correction lenses
    if (propName.includes("prism")) {
      // Check if spherical or cylindrical power indicates need for prism
      const sphValue = currentValues[`property1`]; // Assuming SPH is first
      const cylValue = currentValues[`property2`]; // Assuming CYL is second

      // Show prism only if specifically high powers or if already selected
      const currentPrismValue = currentValues[`property${index + 1}`];
      return Boolean(currentPrismValue);
    }

    return true;
  };

  // Get required variant count (excluding hidden variants)
  getRequiredVariantCount = () => {
    const item = this.state.selectedItem;
    if (!item || !item.hasVariants) return 0;

    // Parse variant properties from the API structure
    const variantProperties = [];
    const numProperties = item.numApplicableVariantProperties || 0;

    for (let i = 1; i <= numProperties; i++) {
      const name = item[`variantProperty${i}Name`];
      const values = item[`variantProperty${i}Values`];

      if (name && values && values.length > 0) {
        variantProperties.push({
          name: name,
          values: values,
          index: i,
        });
      }
    }

    return variantProperties.filter((prop) => {
      // CYL is optional, do not count it as required
      if ((prop.name || "").toLowerCase().includes("cyl")) return false;
      return this.shouldShowVariantProperty(prop, prop.index - 1);
    }).length;
  };

  // Filter variant options for searchable dropdown
  filterVariantOption = (query, option) => {
    return option.toLowerCase().includes(query.toLowerCase());
  };

  // Render variant option item
  renderVariantOption = (option, { handleClick, modifiers, query }) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }

    return (
      <MenuItem
        active={modifiers.active}
        disabled={modifiers.disabled}
        key={option}
        onClick={handleClick}
        text={this.highlightText(option, query)}
      />
    );
  };

  // Highlight matching text in options
  highlightText = (text, query) => {
    if (!query) return text;

    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;

    return (
      <>
        {text.slice(0, index)}
        <strong>{text.slice(index, index + query.length)}</strong>
        {text.slice(index + query.length)}
      </>
    );
  };

  // Handle search input change with debouncing
  handleSearchChange = _.debounce((searchTerm) => {
    this.setState({ searchTerm }, () => {
      this.searchItems(searchTerm);
    });
  }, 300);

  // Handle category filter change
  handleCategoryChange = (category) => {
    this.setState({ selectedCategory: category }, () => {
      this.searchItems();
    });
  };

  // Clear selection
  clearSelection = () => {
    this.setState({
      selectedItem: null,
      variantValues: {},
      searchTerm: "",
      searchResults: [],
      isVariantSelectionComplete: false,
      showAddButton: false,
    });

    if (this.props.onItemSelect) {
      this.props.onItemSelect(null);
    }
  };

  // Add item to final list
  addItemToList = () => {
    const { selectedItem, variantValues, isVariantSelectionComplete } = this.state;

    if (!selectedItem) return;

    // Check if variants are required but not complete
    if (selectedItem.hasVariants && !isVariantSelectionComplete) {
      cogoToast.error("Please select all required variants before adding this item");
      return;
    }

    // Create final item object
    const finalItem = {
      ...selectedItem,
      selectedVariants: variantValues,
      variantSelectionComplete: isVariantSelectionComplete,
    };

    // Notify parent component
    if (this.props.onItemSelect) {
      this.props.onItemSelect(finalItem);
    }

    // Clear selection for next item
    this.clearSelection();

    // Clear search results after adding item
    this.setState({
      searchTerm: "",
      searchResults: [],
    });

    cogoToast.success(`${selectedItem.itemName} saved`);
  };

  // Render search filters
  renderSearchFilters = () => {
    return (
      <div style={{ marginBottom: "8px" }}>
        <div
          style={{
            display: "flex",
            gap: "8px",
            marginBottom: "6px",
          }}
        >
          {/* Search Input */}
          <InputGroup
            placeholder="Search IOLs..."
            value={this.state.searchTerm}
            onChange={(e) => {
              this.setState({ searchTerm: e.target.value });
              this.handleSearchChange(e.target.value);
            }}
            leftIcon="search"
            rightElement={this.state.isSearching ? <Spinner size={14} style={{ margin: "3px" }} /> : null}
            style={{
              backgroundColor: "var(--bg-surface)",
              color: "var(--text-primary)",
              flex: 1,
            }}
          />

          {/* Category Filter */}
          {/* <HTMLSelect
            value={this.state.selectedCategory}
            onChange={(e) => this.handleCategoryChange(e.target.value)}
            style={{
              backgroundColor: "var(--bg-surface)",
              color: "var(--text-primary)",
            }}
            disabled={this.state.isLoadingCategories}
          >
            <option value="">
              {this.state.isLoadingCategories ? "Loading..." : "All Categories"}
            </option>
            {this.state.availableCategories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </HTMLSelect> */}
        </div>

        {/* Show Inactive Toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <input
            type="checkbox"
            id="showInactive"
            checked={this.state.showInactive}
            onChange={(e) => {
              this.setState({ showInactive: e.target.checked }, () => {
                this.searchItems();
              });
            }}
          />
          <label htmlFor="showInactive" style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
            Show inactive items
          </label>
        </div>
      </div>
    );
  };

  // Render search results
  renderSearchResults = () => {
    if (!this.state.searchTerm.trim() && !this.state.selectedCategory) {
      return null; // Remove the placeholder text entirely
    }

    if (this.state.isSearching) {
      return (
        <NonIdealState
          icon={<Spinner />}
          title="Searching items..."
          description="Please wait while we search for items"
        />
      );
    }

    if (this.state.searchResults.length === 0) {
      return (
        <NonIdealState icon="search" title="No items found" description="Try adjusting your search terms or filters" />
      );
    }

    return (
      <div style={{ maxHeight: "200px", overflowY: "auto" }}>
        {this.state.searchResults.map((item) => (
          <Card
            key={item._id}
            interactive
            onClick={() => this.handleItemSelect(item)}
            style={{
              margin: "2px 0",
              padding: "8px 12px",
              backgroundColor: "var(--bg-surface)",
              border: "1px solid var(--border-color)",
              opacity:
                this.state.selectedItem && this.state.selectedItem.hasVariants && !this.state.isVariantSelectionComplete
                  ? 0.5
                  : 1,
              cursor:
                this.state.selectedItem && this.state.selectedItem.hasVariants && !this.state.isVariantSelectionComplete
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontWeight: "600", color: "var(--text-primary)" }}>{item.itemName}</span>
                <span style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{item.itemCategory}</span>
                {item.hasVariants && (
                  <Tag minimal style={{ fontSize: "10px", padding: "2px 6px" }}>
                    Has Variants
                  </Tag>
                )}
              </div>
              {!item.isActive && (
                <Tag intent="warning" minimal style={{ fontSize: "10px" }}>
                  Inactive
                </Tag>
              )}
            </div>
          </Card>
        ))}
      </div>
    );
  };

  // Render selected item details
  renderSelectedItem = () => {
    if (!this.state.selectedItem) return null;

    const item = this.state.selectedItem;
    const hasVariants = item.hasVariants && item.numApplicableVariantProperties > 0;
    const allVariantsSelected = this.areAllVariantsSelected();

    return (
      <Card
        style={{
          padding: "8px",
          marginBottom: "8px",
          backgroundColor: "var(--bg-surface)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: "600",
                fontSize: "16px",
                color: "var(--text-primary)",
              }}
            >
              Selected: {item.itemName}
            </div>
            <div
              style={{
                color: "var(--text-secondary)",
                marginTop: "2px",
                fontSize: "14px",
              }}
            >
              Category: {item.itemCategory}
              {hasVariants && (
                <span style={{ marginLeft: "8px" }}>
                  • Has {item.numApplicableVariantProperties} Variants
                  {!this.state.isVariantSelectionComplete && (
                    <span style={{ color: "var(--intent-warning)", marginLeft: "5px" }}>(Selection Required)</span>
                  )}
                  {this.state.isVariantSelectionComplete && (
                    <span style={{ color: "var(--intent-success)", marginLeft: "5px" }}>✓ Complete</span>
                  )}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            {/* Save Button - Only show here when there are no variants */}
            {this.state.showAddButton && !hasVariants && (
              <Button intent="primary" icon="floppy-disk" onClick={this.addItemToList} style={{ fontSize: "12px" }}>
                Save
              </Button>
            )}

            {/* Clear Button */}
            <Button minimal icon="cross" onClick={this.clearSelection} style={{ color: "var(--text-secondary)" }} />
          </div>
        </div>
      </Card>
    );
  };

  // Render variant selectors
  renderVariantSelectors = () => {
    const item = this.state.selectedItem;

    if (!item) return null;

    // Check if item has variants
    if (!item.hasVariants) {
      return null;
    }

    // Parse variant properties from the API structure
    const variantProperties = [];
    const numProperties = item.numApplicableVariantProperties || 0;

    for (let i = 1; i <= numProperties; i++) {
      const name = item[`variantProperty${i}Name`];
      const values = item[`variantProperty${i}Values`];

      if (name && values && values.length > 0) {
        variantProperties.push({
          name: name,
          values: values,
          index: i,
        });
      }
    }

    if (variantProperties.length === 0) {
      return null;
    }

    // Determine which properties are visible right now
    const visibleProps = variantProperties.filter((prop) => this.shouldShowVariantProperty(prop, prop.index - 1));
    const firstRowProps = visibleProps.slice(0, 2);
    const remainingProps = visibleProps.slice(2);

    return (
      <Card
        style={{
          padding: "8px",
          marginBottom: "8px",
          backgroundColor: "var(--bg-surface)",
        }}
      >
        <div
          style={{
            marginBottom: "8px",
            fontWeight: "600",
            color: "var(--text-primary)",
            fontSize: "14px",
          }}
        >
          Select Variant Properties:
        </div>
        {/* First row: first two dropdowns + Save button to the right */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            alignItems: "flex-end",
          }}
        >
          {firstRowProps.map((prop) => {
            const selectedValue = this.state.variantValues[`property${prop.index}`] || "";
            const isSph = (prop.name || "").toLowerCase().includes("sph");
            const isCyl = (prop.name || "").toLowerCase().includes("cyl");
            return (
              <div key={prop.name} style={{ minWidth: "150px", maxWidth: "200px" }}>
                <div style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "4px" }}>
                  {prop.name}
                </div>
                {isSph || isCyl ? (
                  <SingleSelectWrapper
                    items={prop.values}
                    selectedItem={selectedValue || null}
                    placeholder={`Search ${prop.name}...`}
                    onChange={(value) => this.handleVariantChange(prop.index - 1, value)}
                    width="200px"
                  />
                ) : prop.values.length > 5 ? (
                  <Suggest
                    items={prop.values}
                    itemPredicate={this.filterVariantOption}
                    itemRenderer={this.renderVariantOption}
                    onItemSelect={(value) => this.handleVariantChange(prop.index - 1, value)}
                    inputValueRenderer={(value) => value}
                    selectedItem={selectedValue}
                    inputProps={{
                      placeholder: `Select ${prop.name}...`,
                      style: {
                        backgroundColor: "var(--bg-surface)",
                        color: "var(--text-primary)",
                        height: "32px",
                        fontSize: "14px",
                      },
                    }}
                    popoverProps={{
                      minimal: true,
                      fill: true,
                    }}
                    noResults={<MenuItem disabled text={`No ${prop.name} found.`} />}
                    resetOnSelect={false}
                    fill
                  />
                ) : (
                  <HTMLSelect
                    value={selectedValue}
                    onChange={(e) => this.handleVariantChange(prop.index - 1, e.target.value)}
                    style={{
                      backgroundColor: "var(--bg-surface)",
                      color: "var(--text-primary)",
                      height: "32px",
                      fontSize: "14px",
                    }}
                    fill
                  >
                    <option value="">Select {prop.name}</option>
                    {prop.values.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </HTMLSelect>
                )}

                {selectedValue && (
                  <div style={{ marginTop: "2px" }}>
                    <Tag
                      intent="primary"
                      minimal
                      onRemove={() => this.handleVariantChange(prop.index - 1, "")}
                      style={{ fontSize: "10px", padding: "2px 6px" }}
                    >
                      {selectedValue}
                    </Tag>
                  </div>
                )}
              </div>
            );
          })}

          {this.state.showAddButton && (
            <Button
              intent="primary"
              icon="floppy-disk"
              onClick={this.addItemToList}
              style={{ fontSize: "12px", height: "32px" }}
            >
              Save
            </Button>
          )}
        </div>

        {/* Remaining dropdowns (if any) */}
        {remainingProps.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              alignItems: "flex-start",
              marginTop: "10px",
            }}
          >
            {remainingProps.map((prop) => {
              const selectedValue = this.state.variantValues[`property${prop.index}`] || "";
              const isSph = (prop.name || "").toLowerCase().includes("sph");
              const isCyl = (prop.name || "").toLowerCase().includes("cyl");
              return (
                <div key={prop.name} style={{ minWidth: "150px", maxWidth: "200px" }}>
                  <div
                    style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "4px" }}
                  >
                    {prop.name}
                  </div>
                  {isSph || isCyl ? (
                    <SingleSelectWrapper
                      items={prop.values}
                      selectedItem={selectedValue || null}
                      placeholder={`Search ${prop.name}...`}
                      onChange={(value) => this.handleVariantChange(prop.index - 1, value)}
                      width="200px"
                    />
                  ) : prop.values.length > 5 ? (
                    <Suggest
                      items={prop.values}
                      itemPredicate={this.filterVariantOption}
                      itemRenderer={this.renderVariantOption}
                      onItemSelect={(value) => this.handleVariantChange(prop.index - 1, value)}
                      inputValueRenderer={(value) => value}
                      selectedItem={selectedValue}
                      inputProps={{
                        placeholder: `Select ${prop.name}...`,
                        style: {
                          backgroundColor: "var(--bg-surface)",
                          color: "var(--text-primary)",
                          height: "32px",
                          fontSize: "14px",
                        },
                      }}
                      popoverProps={{
                        minimal: true,
                        fill: true,
                      }}
                      noResults={<MenuItem disabled text={`No ${prop.name} found.`} />}
                      resetOnSelect={false}
                      fill
                    />
                  ) : (
                    <HTMLSelect
                      value={selectedValue}
                      onChange={(e) => this.handleVariantChange(prop.index - 1, e.target.value)}
                      style={{
                        backgroundColor: "var(--bg-surface)",
                        color: "var(--text-primary)",
                        height: "32px",
                        fontSize: "14px",
                      }}
                      fill
                    >
                      <option value="">Select {prop.name}</option>
                      {prop.values.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </HTMLSelect>
                  )}

                  {selectedValue && (
                    <div style={{ marginTop: "2px" }}>
                      <Tag
                        intent="primary"
                        minimal
                        onRemove={() => this.handleVariantChange(prop.index - 1, "")}
                        style={{ fontSize: "10px", padding: "2px 6px" }}
                      >
                        {selectedValue}
                      </Tag>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    );
  };

  render() {
    return (
      <div
        style={{
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
        }}
      >
        {/* Simple Item Selection Dropdown */}
        <Card
          style={{
            padding: "8px",
            backgroundColor: "var(--bg-surface)",
            marginBottom: "8px",
          }}
        >
          {/* Search Filters */}
          {this.renderSearchFilters()}

          {/* Search Results */}
          {this.renderSearchResults()}
        </Card>

        {/* Selected Item Display */}
        {this.renderSelectedItem()}

        {/* Variant Selectors - Only show if item has variants */}
        {this.renderVariantSelectors()}
      </div>
    );
  }
}

export default inject("authStore")(observer(ItemSelectionDropdown));
